import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AS2_CONNECTION_STATUS_CHECK } from '../../graphql/indicators';
import ConnectionIndicator from './ConnectionIndicator';
import { CHECKING, ERROR } from '../../utils/ConnectionStatuses';
import { getPublicConfig } from '../../reducers';
import { isEexCustomer } from '../../utils/customer';

// Refresh cadence for the AS2 health indicator. Backed by a backend-side TTL
// cache on as2ConnectionStatusCheck (application.as2.ping.cache.ttl-seconds),
// so the real outbound AS2 ping rate stays cache-bound regardless of how many
// users have the indicator visible.
const AS2_STATUS_POLL_INTERVAL_MS = 60_000;

const indicatorPropTypes = {
    className: PropTypes.string,
    message: PropTypes.string,
};

// Outer wrapper performs the EEX gate without touching Apollo, so callers that
// don't provide an Apollo client (e.g. the Members/CashLimits loading-state
// tests that mock useExtendedQuery) still render cleanly for non-EEX setups.
function AS2ConnectionIndicator(props) {
    const { customer } = useSelector(getPublicConfig);
    if (!isEexCustomer(customer)) {
        return null;
    }
    return <AS2ConnectionIndicatorImpl {...props} />;
}
AS2ConnectionIndicator.propTypes = indicatorPropTypes;

function AS2ConnectionIndicatorImpl(props) {
    const {
        className = '',
        message,
    } = props;
    const { t } = useTranslation();

    const { loading, error, data } = useQuery(AS2_CONNECTION_STATUS_CHECK, {
        fetchPolicy: 'network-only',
        pollInterval: AS2_STATUS_POLL_INTERVAL_MS,
    });

    let status;
    if (loading) {
        status = CHECKING.serverStatus;
    } else if (error) {
        status = ERROR.serverStatus;
    } else {
        status = data.as2ConnectionStatusCheck;
    }

    return (
        <ConnectionIndicator
            className={className}
            message={message ? message : t('indicator.as2-message')}
            status={status}
        />
    );
}
AS2ConnectionIndicatorImpl.propTypes = indicatorPropTypes;

export default AS2ConnectionIndicator;
